<template>
  <main class="login">
    <form>
      <section>
        <h1 class="pg_title">{{ $_translations.value.page_login_title }}</h1>

        <TransparentCard>
          <div class="input-container">
            <BaseTextFormInput
              :model-value="formValues.username.state"
              :label="$_translations.value.page_login_input_label_phone_number"
              :error="formValues.username.validation.error"
              :error-message="$_translations.value[formValues.username.validation.message]"
              @update:modelValue="formValues.username.setState"
            />
          </div>

          <div class="input-container">
            <BasePasswordFormInput
              :model-value="formValues.password.state"
              :label="$_translations.value.page_login_input_label_password"
              :error="formValues.password.validation.error"
              :error-message="$_translations.value[formValues.password.validation.message]"
              @update:modelValue="formValues.password.setState"
            />
          </div>

          <BaseCheckboxFormInput
            v-model="formValues.rememberMe.state"
            :label="$_translations.value.common_input_label_remember_me"
          />

          <div class="info_links">
            <div class="flex-row">
              <a href="#" @click.prevent="redirectToForgotPasswordPage">
                {{ $_translations.value.page_login_forgot_pin }}
              </a>
            </div>

            <div class="pin_forgot flex-row">
              <BaseImage src="/static/images/info.svg" alt="info" />

              <a href="#" @click.prevent="openPinModal">
                {{ $_translations.value.page_login_what_my_pin }}
              </a>
            </div>
          </div>

          <BaseButton
            container-class="finish"
            :disabled="submitting"
            @click:button="handleSubmit"
          >
            {{ $_translations.value.page_login_button_finish }}
          </BaseButton>
        </TransparentCard>

        <p class="last_line login">
          {{ $_translations.value.page_login_new_here }}
          <a
            href="#"
            class="text_links login"
            @click.prevent="showRegistration"
          >
            {{ $_translations.value.page_login_create_account }}
          </a>
        </p>
      </section>
    </form>
  </main>

  <RegisterViaSmsModal
    v-if="isRegisterViaSmsModalOpen"
    @redirect:loginPage="redirectToLoginPageFromRegisterModal"
  />

  <PinModal
    v-if="isPinModalOpen"
    @redirect:forgotPassword="handleForgotPasswordRedirectFromPinModal"
  />
</template>

<script setup>
import BaseImage from '@/components/image/BaseImage.vue';
import BaseButton from '@/components/button/BaseButton.vue';
import BaseTextFormInput from '@/components/formInput/BaseTextFormInput.vue';
import BasePasswordFormInput from '@/components/formInput/BasePasswordFormInput.vue';
import BaseCheckboxFormInput from '@/components/formInput/BaseCheckboxFormInput.vue';
import TransparentCard from '@/components/card/TransparentCard.vue';
import RegisterViaSmsModal from '@/components/modal/RegisterViaSmsModal.vue';
import PinModal from '@/views/login/components/PinModal.vue';
import useAuthStore from '@/store/modules/auth.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';
import useFormHook from '@/hooks/useForm.js';
import useUILockHook from '@/hooks/useUILock.js';
import useForgotPasswordHook from '@/hooks/useForgotPassword.js';
import useRegisterHook from '@/hooks/useRegister.js';
import { getApiResponseErrorMessage } from '@/plugins/api.js';
import { loginErrorMessagesPerResponseType } from '@/constants/login.js';

const authStore = useAuthStore();

const { redirectToHomePage } = usePageNavigationHook();
const [isPinModalOpen, openPinModal, closePinModal] = useModalHook();
const { formValues, isFormInvalid, resetFormErrors } = useFormHook({
  username: '',
  password: '',
  rememberMe: true,
});
const [submitting, setSubmittingState] = useUILockHook();
const { redirectToIndexStep: redirectToForgotPasswordPage } = useForgotPasswordHook();
const {
  isRegisterViaSmsModalOpen,
  redirectToIndexStep: showRegistration,
  redirectToLoginPage: redirectToLoginPageFromRegisterModal,
} = useRegisterHook();

const validateForm = () => {
  if (!formValues.username.state) {
    formValues.username.setValidation({ error: true, message: 'common_error_input_field_required' });
  }

  if (!formValues.password.state) {
    formValues.password.setValidation({ error: true, message: 'common_error_input_field_required' });
  }

  return isFormInvalid();
};

const handleSubmit = async () => {
  try {
    setSubmittingState(true);

    resetFormErrors();

    const invalid = validateForm();

    if (invalid) { return; }

    await authStore.dispatchLogin({
      payload: {
        username: formValues.username.state,
        password: formValues.password.state,
        rememberMe: formValues.rememberMe.state,
      },
    });

    redirectToHomePage();
  } catch (error) {
    formValues.username.setValidation({ error: true, message: '' });
    formValues.password.setValidation({
      error: true,
      message: getApiResponseErrorMessage(error?.responseEnum, loginErrorMessagesPerResponseType),
    });
  } finally {
    setSubmittingState(false);
  }
};

const handleForgotPasswordRedirectFromPinModal = () => {
  closePinModal({
    immediate: true,
    type: 'useCallback',
    afterHideCallback: redirectToForgotPasswordPage,
  });
};
</script>
